import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader, Trash2, Upload } from "lucide-react";
import { getToken } from "../../app/auth/authConfig";
import Select from "react-select";
import { getTagOptions } from "./tagStorage";

const ToolRegistration = () => {
  const [toolName, setToolName] = useState("");
  const [tooltype, setCategoryType] = useState("");
  const [toolDescription, setToolDescription] = useState("");
  const [descError, setDescError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [tags, setTags] = useState([]);
  const [toolUrl, setToolUrl] = useState("");
  const [groupId, setGroupId] = useState("");
  const [reportId, setReportId] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [rlsEnabled, setRlsEnabled] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState([]);
  const [onboardingFile, setOnboardingFile] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [toast, setToast] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);

  const isPowerBI = tooltype === "Embedded PowerBi Report";

  useEffect(() => {
    if (isPowerBI && groupId && reportId) {
      setToolUrl(
        `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${groupId}`
      );
    }
  }, [groupId, reportId, isPowerBI]);

  useEffect(() => {
    const loadTags = async () => {
      const options = await getTagOptions();
      setTagOptions(options || []);
    };
    loadTags();

    document.addEventListener("aays-tags-updated", loadTags);
    return () => {
      document.removeEventListener("aays-tags-updated", loadTags);
    };
  }, []);

  const handleTagsChange = (selectedOptions) => {
    setTags(selectedOptions || []);
  };

  const handleFileSelection = (event, setter, errorKey) => {
    const selectedFiles = Array.from(event.target.files || []);
    setter(selectedFiles);
    setFormErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[errorKey];
      return nextErrors;
    });
    event.target.value = "";
  };

  const removeSelectedFile = (files, setter, removeIndex, errorKey) => {
    const updatedFiles = files.filter((_, index) => index !== removeIndex);
    setter(updatedFiles);
    if (updatedFiles.length === 0) {
      setFormErrors((prev) => ({
        ...prev,
        [errorKey]: "At least one file is required",
      }));
    }
  };

  const clearSelectedFiles = (setter, errorKey) => {
    setter([]);
    setFormErrors((prev) => ({
      ...prev,
      [errorKey]: "At least one file is required",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    // Required field checks
    if (!toolName.trim()) errors.toolName = "Tool Name is required";
    if (!tooltype.trim()) errors.tooltype = "Category Type is required";
    if (!toolUrl.trim()) errors.toolUrl = "Tool URL is required";
    if (!tags.length) errors.tags = "At least one tag is required";
    if (!uploadFile.length) errors.uploadFile = "Product document upload is required";
    if (!onboardingFile.length) errors.onboardingFile = "Onboarding file upload is required";
    if (!ownerEmail.trim()) errors.ownerEmail = "Owner Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim())) errors.ownerEmail = "Enter a valid email address";
    // Word count validation
    const wordCount = toolDescription.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 500) {
      errors.toolDescription = `Description must be at least 500 words. Currently: ${wordCount}`;
    }
    if (!toolDescription.trim()) errors.toolDescription = "Description is required";
    setFormErrors(errors);
    setDescError(errors.toolDescription || "");
    if (Object.keys(errors).length > 0) return;

    const formData = new FormData();
    formData.append("toolName", toolName);
    formData.append("tooltype", tooltype);
    formData.append("description", toolDescription); // Markdown and newlines are preserved
    formData.append("tags", tags.map(t => t.value).join(","));
    formData.append("url", toolUrl);
    formData.append("groupId", isPowerBI ? groupId : null);
    formData.append("reportId", isPowerBI ? reportId : null);
    formData.append("datasetId", isPowerBI ? datasetId : null);
    formData.append("rlsEnabled", isPowerBI ? rlsEnabled : false);
    formData.append("roles", isPowerBI && rlsEnabled ? JSON.stringify(roles) : JSON.stringify([]));
    formData.append("ownerEmail", ownerEmail.trim());
    
    // Append multiple files for Product Documents
    if (uploadFile.length) {
      uploadFile.forEach((file) => {
        formData.append("productDocuments", file);
      });
    }

    // Append multiple files for Onboarding Files
    if (onboardingFile.length) {
      onboardingFile.forEach((file) => {
        formData.append("onboardingFiles", file);
      });
    }

    try {
      setLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/`;
      const token = await getToken();

      await axios.post(baseURL, formData, {
        headers: {
          "oidc-token": token,
          "Content-Type": "multipart/form-data",
        },
      });
      // Show success toast
      setToast({ type: "success", message: "✅ Tool registered successfully!" });
      setTimeout(() => setToast(null), 3000);
      handleReset();
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Tool registration failed";
      // Show error toast
      setToast({ type: "error", message: `❌ ${errorMsg}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setToolName("");
    setCategoryType("");
    setToolDescription("");
    setDescError("");
    setFormErrors({});
    setTags([]);
    setToolUrl("");
    setGroupId("");
    setReportId("");
    setDatasetId("");
    setRlsEnabled(false);
    setRoles([]);
    setUploadFile([]);
    setOnboardingFile([]);
    setOwnerEmail("");
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/categories/`;
      const token = await getToken();
      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      setCategories(res.data);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const categoryOptions = categories?.map((c) => c.categoryName) || [];

  const roleOptions = ["Admin", "User", "Linemanager", "Employee"];

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: 32 }}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", gap: 24, flexWrap: "wrap" }}
      >
        {/* Owner Email */}
        <div style={{ flex: "1 1 30%" }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Owner Email <span style={{ color: "red" }}>*</span>
          </label>
          <input
            placeholder="Enter Owner Email"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
            disabled={loading || categoriesLoading}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 16,
              height: 48,
              backgroundColor: (loading || categoriesLoading) ? "#f0f0f0" : "white",
              cursor: (loading || categoriesLoading) ? "not-allowed" : "auto",
            }}
          />
          {formErrors.ownerEmail && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.ownerEmail}</div>}
        </div>

        {/* Tool Name */}
        <div style={{ flex: "1 1 30%" }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Tool Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            placeholder="Enter your Tool Name"
            type="text"
            value={toolName}
            onChange={(e) => setToolName(e.target.value)}
            required
            disabled={loading || categoriesLoading}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 16,
              height: 48,
              backgroundColor: (loading || categoriesLoading) ? "#f0f0f0" : "white",
              cursor: (loading || categoriesLoading) ? "not-allowed" : "auto",
            }}
          />
          {formErrors.toolName && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.toolName}</div>}
        </div>

        {/* Category Type */}
        <div style={{ flex: "1 1 30%" }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Category Type <span style={{ color: "red" }}>*</span>
          </label>
          {categoriesLoading ? (
            <div style={{
              width: "100%",
              height: "48px",
              background: "#f0f0f0",
              borderRadius: "4px",
              border: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
            }}>
              <div className="h-4 bg-gray-300 rounded w-full animate-pulse" />
            </div>
          ) : (
            <select
              value={tooltype}
              onChange={(e) => setCategoryType(e.target.value)}
              required
              disabled={loading}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 16,
                height: 48,
                background: loading ? "#f0f0f0" : "#fff",
                cursor: loading ? "not-allowed" : "auto",
              }}
            >
              <option value="">Select Category</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
          {formErrors.tooltype && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.tooltype}</div>}
        </div>

        {/* Tool URL */}
        {!isPowerBI && (
          <div style={{ flex: "1 1 30%" }}>
            <label
              style={{ fontWeight: 500, marginBottom: 8, display: "block" }}
            >
              Tool URL <span style={{ color: "red" }}>*</span>
            </label>
            <input
              placeholder="Enter your Tool URL"
              type="url"
              value={toolUrl}
              onChange={(e) => setToolUrl(e.target.value)}
              required
              disabled={loading}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 16,
                height: 48,
              }}
            />
            {formErrors.toolUrl && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.toolUrl}</div>}
          </div>
        )}

        {/* Tags */}
        <div style={{ flex: "1 1 30%" }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Tags <span style={{ color: "red" }}>*</span>
          </label>
          <Select
            isMulti
            options={tagOptions}
            value={tags}
            onChange={handleTagsChange}
            isDisabled={loading}
            placeholder="Select tags..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 48,
                borderColor: "#ccc",
                fontSize: 16,
                background: loading ? "#f0f0f0" : "#fff",
                cursor: loading ? "not-allowed" : "auto",
              }),
            }}
          />
          {formErrors.tags && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.tags}</div>}
        </div>

        {/* PowerBI Fields */}
        {isPowerBI && (
          <>
            <div style={{ flex: "1 1 30%" }}>
              <label style={{ fontWeight: 500, marginBottom: 8 }}>
                Group ID <span style={{ color: "red" }}>*</span>
              </label>
              <input
                placeholder="Enter your Group ID"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: 16,
                  height: 48,
                }}
              />
            </div>

            <div style={{ flex: "1 1 30%" }}>
              <label style={{ fontWeight: 500, marginBottom: 8 }}>
                Report ID <span style={{ color: "red" }}>*</span>
              </label>
              <input
                placeholder="Enter your Report ID"
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: 16,
                  height: 48,
                }}
              />
            </div>

            <div style={{ flex: "1 1 30%" }}>
              <label style={{ fontWeight: 500, marginBottom: 8 }}>
                Dataset ID <span style={{ color: "red" }}>*</span>
              </label>
              <input
                placeholder="Enter your DatasetID"
                value={datasetId}
                onChange={(e) => setDatasetId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: 16,
                  height: 48,
                }}
              />
            </div>

            <div
              style={{
                flex: "1 1 30%",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <input
                type="checkbox"
                checked={rlsEnabled}
                onChange={(e) => setRlsEnabled(e.target.checked)}
              />
              <label>Enable RLS</label>
            </div>

            {/* Roles when RLS enabled */}
            {rlsEnabled && (
              <div style={{ flex: "1 1 30%" }}>
                <label style={{ fontWeight: 500, marginBottom: 8 }}>
                  RLS Roles <span style={{ color: "red" }}>*</span>
                </label>

                <select
                  multiple
                  value={roles}
                  onChange={e => setRoles(Array.from(e.target.selectedOptions, option => option.value))}
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    fontSize: 16,
                    height: 48,
                    background: "#fff",
                  }}
                >
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Product Documents Upload */}
        <div style={{ flex: "1 1 100%" }}>
          <label htmlFor="file-input" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Product Documents (PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX) <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="file-input"
            type="file"
            multiple
            onChange={(e) => handleFileSelection(e, setUploadFile, "uploadFile")}
            disabled={loading}
            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
            style={{ display: "none" }}
          />
          <div
            style={{
              border: formErrors.uploadFile ? "1px solid #ef4444" : "1px dashed #c084fc",
              background: loading ? "#f5f5f5" : "linear-gradient(180deg, #fdf4ff 0%, #ffffff 100%)",
              borderRadius: 12,
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 600, color: "#4b5563", marginBottom: 4 }}>
                  {uploadFile.length ? `${uploadFile.length} product file(s) selected` : "No product files selected yet"}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  Drag-and-drop is not enabled here. Use the chooser to add one or more files.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <label
                  htmlFor="file-input"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: loading ? "#e5e7eb" : "#bb1ccc",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  <Upload size={16} />
                  Choose files
                </label>
                {!!uploadFile.length && (
                  <button
                    type="button"
                    onClick={() => clearSelectedFiles(setUploadFile, "uploadFile")}
                    disabled={loading}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#dc2626",
                      fontWeight: 600,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    <Trash2 size={16} />
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {!!uploadFile.length && (
              <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                {uploadFile.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${index}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      backgroundColor: "#fff",
                      border: "1px solid #f3e8ff",
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {file.name}
                      </div>
                      <div style={{ color: "#6b7280", fontSize: 12 }}>
                        {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(uploadFile, setUploadFile, index, "uploadFile")}
                      disabled={loading}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        border: "none",
                        background: "#fee2e2",
                        color: "#b91c1c",
                        borderRadius: 8,
                        padding: "8px 10px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: 600,
                      }}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formErrors.uploadFile && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.uploadFile}</div>}
        </div>

        {/* Onboarding File Upload */}
        <div style={{ flex: "1 1 100%" }}>
          <label htmlFor="onboarding-file-input" style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Onboarding Files (PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX) <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="onboarding-file-input"
            type="file"
            multiple
            onChange={(e) => handleFileSelection(e, setOnboardingFile, "onboardingFile")}
            disabled={loading}
            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
            style={{ display: "none" }}
          />
          <div
            style={{
              border: formErrors.onboardingFile ? "1px solid #ef4444" : "1px dashed #c084fc",
              background: loading ? "#f5f5f5" : "linear-gradient(180deg, #fdf4ff 0%, #ffffff 100%)",
              borderRadius: 12,
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 600, color: "#4b5563", marginBottom: 4 }}>
                  {onboardingFile.length ? `${onboardingFile.length} onboarding file(s) selected` : "No onboarding files selected yet"}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  Add the onboarding pack here and remove individual files if needed.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <label
                  htmlFor="onboarding-file-input"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: loading ? "#e5e7eb" : "#bb1ccc",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  <Upload size={16} />
                  Choose files
                </label>
                {!!onboardingFile.length && (
                  <button
                    type="button"
                    onClick={() => clearSelectedFiles(setOnboardingFile, "onboardingFile")}
                    disabled={loading}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#dc2626",
                      fontWeight: 600,
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    <Trash2 size={16} />
                    Clear all
                  </button>
                )}
              </div>
            </div>
            {!!onboardingFile.length && (
              <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                {onboardingFile.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${index}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      backgroundColor: "#fff",
                      border: "1px solid #f3e8ff",
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {file.name}
                      </div>
                      <div style={{ color: "#6b7280", fontSize: 12 }}>
                        {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(onboardingFile, setOnboardingFile, index, "onboardingFile")}
                      disabled={loading}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        border: "none",
                        background: "#fee2e2",
                        color: "#b91c1c",
                        borderRadius: 8,
                        padding: "8px 10px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: 600,
                      }}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {formErrors.onboardingFile && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.onboardingFile}</div>}
        </div>

        {/* Description */}
        <div style={{ flex: "1 1 100%" }}>
          <label htmlFor="tool-description" style={{ fontWeight: 500, marginBottom: 8 }}>
            Description <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            id="tool-description"
            placeholder={"Enter your Tool Description (min 500 words, markdown supported)"}
            value={toolDescription}
            onChange={(e) => setToolDescription(e.target.value)}
            required
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 16,
              height: 120,
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
            }}
          />
          <div style={{ color: descError ? "#ef4444" : "#666", fontSize: 14, marginTop: 4 }}>
            {descError
              ? descError
              : `Word count: ${toolDescription.trim().split(/\s+/).filter(Boolean).length}`}
          </div>
          {formErrors.toolDescription && <div style={{ color: "#ef4444", fontSize: 13 }}>{formErrors.toolDescription}</div>}
          <div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>
            Markdown supported. Use **bold**, *italic*, lists, etc.
          </div>
        </div>

        {/* Buttons */}
        <div
          style={{
            display: "flex",
            gap: 16,
            width: "100%",
            justifyContent: "flex-start",
          }}
        >
          <button
            type="button"
            onClick={handleReset}
            disabled={loading || categoriesLoading}
            style={{
              background: (loading || categoriesLoading) ? "#f5f5f5" : "#ffffff",
              color: (loading || categoriesLoading) ? "#ccc" : "#bb1ccc",
              border: (loading || categoriesLoading) ? "1px solid #e0e0e0" : "1px solid #bb1ccc",
              borderRadius: 4,
              padding: "8px 32px",
              fontWeight: 600,
              fontSize: 16,
              cursor: (loading || categoriesLoading) ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
            }}
          >
            Reset
          </button>

          <button
            type="submit"
            disabled={loading || categoriesLoading}
            style={{
              background: (loading || categoriesLoading) ? "#d4a5e6" : "#bb1ccc",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "8px 32px",
              fontWeight: 600,
              fontSize: 16,
              cursor: (loading || categoriesLoading) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Registering..." : "Register"}
          </button>
        </div>
      </form>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "16px 24px",
            borderRadius: "8px",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            color: "white",
            fontSize: "14px",
            fontWeight: "600",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideIn 0.3s ease",
            zIndex: 9999,
          }}
        >
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default ToolRegistration;
